// 🔬 B251 (AI_115) — MAPA DE COMPLETUDE (UI) — "o que falta preencher na coorte"
// ─────────────────────────────────────────────────────────────────────────────
// Versão de COORTE do checklist da AbaAuditoria. Mostra, por campo-chave, quantos
// % dos pacientes têm o dado e quantos faltam (barra), e o drill-down: clicar num
// campo lista os pacientes SEM aquele dado (visão de GESTÃO identificada, com
// nome — médico-only, igual à lista "Pacientes da coorte"). Reuso da
// TabelaPacientesPesquisa. Código conta; sem IA.
import React, { useMemo, useState } from "react";
import { ClipboardCheck, ChevronRight, Filter, AlertTriangle, Users } from "lucide-react";
import { theme } from "../../../shared/styles/theme";
import { RegistroPesquisa } from "../types";
import { CAMPOS_COMPLETUDE } from "../data/camposCompletude";
import {
  calcularCompletude,
  pacientesSemCampo,
  CompletudeCampo,
} from "../services/completude";
import TabelaPacientesPesquisa, { LinhaPaciente } from "./TabelaPacientesPesquisa";

const card: React.CSSProperties = {
  background: "white",
  border: `1px solid ${theme.gray[200]}`,
  borderRadius: theme.borderRadius.lg,
  padding: "16px",
  marginBottom: "16px",
  fontFamily: theme.fonts.sans,
};

// Verde ≥80, âmbar ≥50, vermelho abaixo — quanto mais preenchido, melhor.
function corPct(pct: number): string {
  if (pct >= 80) return "#16a34a";
  if (pct >= 50) return "#f59e0b";
  return "#dc2626";
}

export default function MapaCompletude({
  registros,
  pacientesPorId,
  onAbrir,
  resumoFiltros,
  onIrParaFiltros,
}: {
  registros: RegistroPesquisa[]; // a coorte (já filtrada no parent)
  pacientesPorId: Map<string, any>;
  onAbrir: (id: string) => void;
  resumoFiltros: string; // eco read-only dos filtros ativos
  onIrParaFiltros: () => void; // volta para a aba de filtros
}) {
  const resultado = useMemo(
    () => calcularCompletude(registros, CAMPOS_COMPLETUDE),
    [registros]
  );

  // Campo selecionado p/ drill-down (chave). Toggle ao clicar de novo.
  const [campoSel, setCampoSel] = useState<string>("");

  const linhasFaltando: LinhaPaciente[] = useMemo(() => {
    if (!campoSel) return [];
    return pacientesSemCampo(registros, campoSel)
      .map((r) => {
        const id = r._pacienteId as string;
        const p = pacientesPorId.get(id);
        if (!p) return null;
        return {
          id,
          nome: p.nome || "(sem nome)",
          grupo: (r.grupo as string) || null,
          status: (r.statusTransplante as string) || null,
          rgct: p.inscricaoTx?.numeroRGCT || p.numeroRGCT || p.rgct || "",
        } as LinhaPaciente;
      })
      .filter((x): x is LinhaPaciente => x !== null);
  }, [campoSel, registros, pacientesPorId]);

  const campoSelLabel =
    CAMPOS_COMPLETUDE.find((c) => c.chave === campoSel)?.label ?? "";

  if (registros.length === 0) {
    return (
      <div style={{ ...card, textAlign: "center", color: theme.gray[500] }}>
        Nenhum paciente na coorte. Ajuste os filtros na aba{" "}
        <button
          onClick={onIrParaFiltros}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: theme.primary[700],
            fontWeight: 600,
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Pesquisa (coorte)
        </button>
        .
      </div>
    );
  }

  return (
    <div>
      {/* Cabeçalho + explicação */}
      <div style={{ ...card, marginBottom: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <ClipboardCheck size={18} color={theme.primary[600]} />
          <strong style={{ fontSize: "15px", color: theme.gray[800] }}>
            Completude de dados — o que falta preencher
          </strong>
        </div>
        <p style={{ fontSize: "12px", color: theme.gray[600], margin: 0, lineHeight: 1.6 }}>
          Para cada campo-chave, quantos pacientes da coorte <strong>têm</strong> o dado e quantos{" "}
          <strong>faltam</strong>. Clique num campo para ver a <strong>lista de pacientes</strong> sem
          aquele dado (com nome — visão de gestão, médico) e abrir o prontuário para completar.
        </p>
        <div
          style={{
            marginTop: "8px",
            fontSize: "11px",
            color: theme.status.warning.text,
            background: theme.status.warning.bg,
            border: `1px solid ${theme.status.warning.border}`,
            borderRadius: theme.borderRadius.md,
            padding: "8px 10px",
            lineHeight: 1.5,
          }}
        >
          <AlertTriangle size={12} style={{ marginRight: "4px", verticalAlign: "middle" }} />
          Avalie sempre uma coorte coerente: alguns campos só fazem sentido em certos grupos (ex.:
          MELD/Child-Pugh e descompensação em cirrótico pré-Tx; estadiamento de CHC só em pacientes
          com CHC). Filtre a coorte antes de ler o mapa.
        </div>
      </div>

      {/* Eco read-only dos filtros ativos */}
      <div
        style={{
          ...card,
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <Filter size={15} color={theme.gray[500]} />
        <span style={{ fontSize: "12px", color: theme.gray[600] }}>
          Coorte: <strong>{resultado.total}</strong> paciente(s) — {resumoFiltros}
        </span>
        <button
          onClick={onIrParaFiltros}
          style={{
            marginLeft: "auto",
            padding: "5px 12px",
            background: "white",
            color: theme.primary[700],
            border: `1px solid ${theme.primary[300]}`,
            borderRadius: theme.borderRadius.md,
            fontSize: "12px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Ajustar filtros
        </button>
      </div>

      {/* Completude geral */}
      <div style={{ ...card, marginBottom: "12px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <span style={{ fontSize: "14px", fontWeight: 700, color: theme.gray[800] }}>
            Completude geral da coorte
          </span>
          <span style={{ fontSize: "22px", fontWeight: 800, color: corPct(resultado.pctGeral) }}>
            {resultado.pctGeral}%
          </span>
        </div>
        <Barra pct={resultado.pctGeral} />
        <div style={{ marginTop: "6px", fontSize: "11px", color: theme.gray[500] }}>
          Média de preenchimento dos {resultado.nCampos} campos-chave em {resultado.total} paciente(s).
        </div>
      </div>

      {/* Mapa por grupo */}
      {resultado.grupos.map((g) => (
        <div key={g.grupo} style={card}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <span style={{ fontSize: "13px", fontWeight: 700, color: theme.gray[700] }}>
              {g.grupo}
            </span>
            {g.faltandoTotal > 0 && (
              <span style={{ fontSize: "11px", color: theme.gray[500] }}>
                {g.faltandoTotal} preenchimento(s) faltando
              </span>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {g.campos.map((c) => (
              <LinhaCampo
                key={c.chave}
                campo={c}
                selecionado={campoSel === c.chave}
                onClick={() => setCampoSel((atual) => (atual === c.chave ? "" : c.chave))}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Drill-down: pacientes sem o campo selecionado */}
      {campoSel && (
        <div style={{ ...card, border: `1px solid ${theme.primary[300]}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
            <Users size={16} color={theme.primary[600]} />
            <strong style={{ fontSize: "14px", color: theme.gray[800] }}>
              Pacientes sem “{campoSelLabel}”
            </strong>
            <span style={{ fontSize: "11px", color: theme.gray[400] }}>
              visão de gestão identificada (com nome) — médico; não sai no export de pesquisa
            </span>
            <button
              onClick={() => setCampoSel("")}
              style={{
                marginLeft: "auto",
                padding: "4px 10px",
                background: "white",
                color: theme.gray[600],
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Fechar
            </button>
          </div>
          {linhasFaltando.length === 0 ? (
            <p style={{ fontSize: "13px", color: "#16a34a", margin: "8px 0 0" }}>
              ✓ Todos os pacientes da coorte têm este dado.
            </p>
          ) : (
            <div style={{ marginTop: "10px" }}>
              <TabelaPacientesPesquisa
                linhas={linhasFaltando}
                onAbrir={onAbrir}
                colunas={["grupo", "status", "rgct"]}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Linha de um campo: rótulo + barra + contagem + chevron, clicável.
function LinhaCampo({
  campo,
  selecionado,
  onClick,
}: {
  campo: CompletudeCampo;
  selecionado: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        width: "100%",
        padding: "8px 10px",
        background: selecionado ? theme.primary[50] : theme.gray[50],
        border: `1px solid ${selecionado ? theme.primary[300] : theme.gray[200]}`,
        borderRadius: theme.borderRadius.md,
        cursor: "pointer",
        textAlign: "left",
        fontFamily: theme.fonts.sans,
      }}
    >
      <span style={{ fontSize: "13px", color: theme.gray[800], flex: "0 0 200px", fontWeight: 600 }}>
        {campo.label}
      </span>
      <div style={{ flex: 1, minWidth: "80px" }}>
        <Barra pct={campo.pct} />
      </div>
      <span style={{ fontSize: "12px", fontWeight: 700, color: corPct(campo.pct), width: "42px", textAlign: "right" }}>
        {campo.pct}%
      </span>
      <span
        style={{
          fontSize: "12px",
          color: campo.faltando > 0 ? "#dc2626" : "#16a34a",
          width: "92px",
          textAlign: "right",
        }}
      >
        {campo.faltando > 0 ? `${campo.faltando} faltando` : "completo"}
      </span>
      <ChevronRight
        size={15}
        color={selecionado ? theme.primary[600] : theme.gray[400]}
        style={{ transform: selecionado ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}
      />
    </button>
  );
}

function Barra({ pct }: { pct: number }) {
  return (
    <div style={{ background: theme.gray[200], borderRadius: "6px", height: "8px", overflow: "hidden" }}>
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          background: corPct(pct),
          borderRadius: "6px",
          transition: "width 0.4s ease",
        }}
      />
    </div>
  );
}
